import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { getTrips, createTrip, deleteTrip } from '../api/trips';
import { format, parseISO, differenceInDays } from 'date-fns';
import './TripsPage.css';

function TripCard({ trip, onDelete, onClick }) {
  const [deleting, setDeleting] = useState(false);
  const startDate = parseISO(trip.start_date);
  const endDate = parseISO(trip.end_date);
  const duration = differenceInDays(endDate, startDate) + 1;

  const handleDelete = async (e) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      `Are you sure you want to delete "${trip.name}"? This will delete all documents and timeline events.`
    );
    if (!confirmed) return;
    setDeleting(true);
    try {
      await onDelete(trip.id);
    } finally {
      setDeleting(false);
    }
  };

  const typeEmoji = {
    flight: '✈️', hotel: '🏨', car_rental: '🚗', transfer: '🚕', activity: '🎯', other: '📋',
  };

  return (
    <div className="trip-card" onClick={onClick}>
      <div className="trip-card-header">
        {trip.destination && (
          <div className="trip-destination-badge">
            <span className="trip-dest-icon">📍</span>
            {trip.destination}
          </div>
        )}
        <button
          className="btn-delete-trip"
          onClick={handleDelete}
          disabled={deleting}
          title="Delete trip"
          aria-label="Delete trip"
        >
          {deleting ? '...' : '🗑️'}
        </button>
      </div>

      <h2 className="trip-card-name">{trip.name}</h2>

      <div className="trip-dates">
        <span className="trip-date-icon">📅</span>
        <span>
          {format(startDate, 'MMM d')} – {format(endDate, 'MMM d, yyyy')}
        </span>
        <span className="trip-duration">· {duration} day{duration !== 1 ? 's' : ''}</span>
      </div>

      <div className="trip-card-footer">
        <div className="trip-stats">
          <span className="trip-stat">
            <span className="stat-icon">📄</span>
            {trip.document_count || 0} doc{trip.document_count !== 1 ? 's' : ''}
          </span>
          <span className="trip-stat">
            <span className="stat-icon">📌</span>
            {trip.event_count || 0} event{trip.event_count !== 1 ? 's' : ''}
          </span>
        </div>
        <span className="trip-view-btn">View →</span>
      </div>
    </div>
  );
}

function CreateTripModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    name: '', start_date: '', end_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_date || !form.end_date) {
      return setError('All fields are required');
    }
    if (form.end_date < form.start_date) {
      return setError('End date must be after start date');
    }
    setError('');
    setLoading(true);
    try {
      await onCreate(form);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create trip');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">New Trip</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label className="form-label">Trip Name</label>
            <input
              className="form-input"
              placeholder="e.g. Poland Adventure 2025"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              autoFocus
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-input"
                value={form.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-input"
                value={form.end_date}
                onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
              />
            </div>
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Trip ✈️'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TripsPage() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadTrips();
  }, []);

  const loadTrips = async () => {
    try {
      const data = await getTrips();
      setTrips(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (form) => {
    const newTrip = await createTrip(form);
    setTrips(prev => [newTrip, ...prev]);
  };

  const handleDelete = async (id) => {
    await deleteTrip(id);
    setTrips(prev => prev.filter(t => t.id !== id));
  };

  return (
    <div className="trips-page">
      <Navbar />
      <div className="trips-container">
        <div className="trips-header">
          <div>
            <h1 className="trips-title">My Trips</h1>
            <p className="trips-subtitle">
              {trips.length === 0 ? 'Plan your next adventure' : `${trips.length} trip${trips.length !== 1 ? 's' : ''} planned`}
            </p>
          </div>
          <button className="btn-new-trip" onClick={() => setShowModal(true)}>
            + New Trip
          </button>
        </div>

        {loading ? (
          <div className="trips-loading">
            {[1,2,3].map(i => <div key={i} className="trip-skeleton" />)}
          </div>
        ) : trips.length === 0 ? (
          <div className="trips-empty">
            <div className="empty-icon">🌍</div>
            <h2>No trips yet</h2>
            <p>Create your first trip and start uploading travel documents</p>
            <button className="btn-primary" onClick={() => setShowModal(true)}>
              Create a Trip
            </button>
          </div>
        ) : (
          <div className="trips-grid">
            {trips.map(trip => (
              <TripCard
                key={trip.id}
                trip={trip}
                onDelete={handleDelete}
                onClick={() => navigate(`/trips/${trip.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <CreateTripModal
          onClose={() => setShowModal(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}
